import React from "react";

import { Link } from "@components";
import { Language, runShortcut, SLACK_INVITE_URL } from "@utils";

interface Lesson {
  title: string;
  content: React.ReactNode;
  pattern: string;
  savedwork?: string;
  answers: string[];
  target: string;
  followupTitle?: string;
  followup?: React.ReactNode;
  language?: Language;
}

export const ADVANCED_LESSONS: Lesson[] = [
  {
    title: "Semgrep: Flex Mode!",
    content: (
      <div>
        <p>
          This tutorial covers some of Semgrep's newest features, and is mostly
          geared toward experienced Semgrep rule authors.
        </p>
        <p>
          We'll start by reviewing an example from the composition tutorial that
          introduced <code>metavariable-regex</code>.
        </p>
        <p>
          This time we have pre-filled the correct answer to get you started.
        </p>
        <p>
          <em>
            Examine the code, then click the blue <strong>Run</strong> button or
            use <strong>{runShortcut}</strong> to see how it works.
          </em>
        </p>
      </div>
    ),
    pattern: `rules:
  - id: use-decimalfield-for-money
    patterns:
    - pattern-inside: |
        class $M(...):
          ...
    - pattern: $F = django.db.models.FloatField(...)
    - metavariable-regex:
        metavariable: '$F'
        regex: '.*(price|fee|salary).*'
    message: Found a FloatField used for variable $F. Use DecimalField for currency fields to avoid float-rounding errors.
    languages: [python]
    severity: ERROR
  `,
    answers: [
      `rules:
  - id: use-decimalfield-for-money
    patterns:
    - pattern-inside: |
        class $M(...):
          ...
    - pattern: $F = django.db.models.FloatField(...)
    - metavariable-regex:
        metavariable: '$F'
        regex: '.*(price|fee|salary).*'
    message: Found a FloatField used for variable $F. Use DecimalField for currency fields to avoid float-rounding errors.
    languages: [python]
    severity: ERROR`,
      `rules:
  - id: use-decimalfield-for-money
    patterns:
    - pattern-inside: |
        class $M(...):
          ...
    - pattern: $F = django.db.models.FloatField(...)
    - metavariable-regex:
        metavariable: '$F'
        regex: '.*(fee|price|salary).*'
    message: Found a FloatField used for variable $F. Use DecimalField for currency fields to avoid float-rounding errors.
    languages: [python]
    severity: ERROR`,
      `rules:
  - id: use-decimalfield-for-money
    patterns:
    - pattern-inside: |
        class $M(...):
          ...
    - pattern: $F = django.db.models.FloatField(...)
    - metavariable-regex:
        metavariable: '$F'
        regex: '.*(fee|salary|price).*'
    message: Found a FloatField used for variable $F. Use DecimalField for currency fields to avoid float-rounding errors.
    languages: [python]
    severity: ERROR`,
    ],
    target: `from django.db import models

class Product(models.Model):
    name = models.CharField(max_length=64)
    description = models.TextField()
    # this is ok
    price = models.DecimalField(max_digits=6, decimal_places=2)
    # this is also ok
    return_rate = models.FloatField()
    # Semgrep finds this because old_fee ends in the word fee, which is in the regex above
    old_fee = models.FloatField()
    # match this
    price_inc = models.FloatField()
`,
    followupTitle: "Correct!",
    followup: (
      <>
        <p>
          Without the <strong>metavariable-regex</strong> here, the metavariable{" "}
          <strong>$F</strong> would match on line 9 as well.
        </p>
        <p>
          However, you can use <strong>metavariable-regex</strong> to restrict
          metavariable matches based on the variable names.
        </p>
        <p>
          You can also use regular expressions directly in semgrep patterns. See
          how in the next example!
        </p>
        <span style={{ fontSize: "3em" }} role="img" aria-label=":train:">
          🚂
        </span>
      </>
    ),
  },
  {
    title: "Regex in patterns",
    content: (
      <div>
        <p>Sometimes, regular expressions are the best tool for the job.</p>
        <p>
          Semgrep lets you combine all of its other tools with regular
          expressions so you can use each where it makes most sense.
        </p>
        <p>
          This example currently tries to use pattern-regex to match{" "}
          <code>baseURL</code> variables that do <em>not</em> start with{" "}
          <code>http://</code> or <code>https://</code> but it doesn't quite
          work.
        </p>
        <p>
          <em>
            Try using <strong>pattern-regex</strong>'s cousin,{" "}
            <strong>pattern-not-regex</strong> to express this more elegantly.
          </em>
        </p>
      </div>
    ),
    pattern: `rules:
  - id: invalid-base-url
    message:
      The 'baseURL' is invalid. This may cause links to not work if deployed. Include the scheme (e.g., https://).
    patterns:
      - pattern: baseURL = "..."
      - pattern-regex: (?!.*http).*
    languages: [generic]`,
    answers: [
      `rules:
  - id: invalid-base-url
    message:
      The 'baseURL' is invalid. This may cause links to not work if deployed. Include the scheme (e.g., https://).
    patterns:
      - pattern: baseURL = "..."
      - pattern-not-regex: https?://.*
    languages: [generic]`,
    ],
    target: `# ruleid: invalid-base-url
baseURL = "example.com"
baseURL = "unfortunateurlwithhttp.com"
baseURL = "http://anotherurl.com"
baseURL = "https://anotherurl.com"
DefaultContentLanguage = "en"
Paginate = 10
enableRobotsTXT = true
`,
    followupTitle: "Terrific!",
    followup: (
      <div>
        <p>
          Regex matching is not the only form of logic that can be built into a
          Semgrep pattern.
        </p>
        <p>
          Keep going for math comparisons like <code>$X {">"} 0</code>!
        </p>
        <p>(Fun fact: 0! = 1)</p>
      </div>
    ),
  },
  {
    title: "Metavariable comparisons",
    content: (
      <div>
        <p>
          Just as metavariable-regex could be used to restrict metavariables
          based on a regex, <code>metavariable-comparison</code> can be used to
          restrict metavariables based on mathematical comparisons.
        </p>
        <p>
          This example currently matches on RSA keys that are initialized with
          2048 bits or fewer.
        </p>
        <p>
          <em>
            Update it to match the message, so it only alarms on RSA keys with
            fewer than 2048 bits.
          </em>
        </p>
      </div>
    ),
    pattern: `rules:
  - id: use-of-weak-rsa-key
    message: RSA keys should be at least 2048 bits based on NIST recommendation.
    patterns:
      - pattern: |
          KeyPairGenerator $KEY = $G.getInstance("RSA");
          ...
          $KEY.initialize($BITS);
      - metavariable-comparison:
          comparison: $BITS <= 2048
          metavariable: $BITS
    languages: [java]
    severity: WARNING`,
    answers: [
      `rules:
  - id: use-of-weak-rsa-key
    message: RSA keys should be at least 2048 bits based on NIST recommendation.
    patterns:
      - pattern: |
          KeyPairGenerator $KEY = $G.getInstance("RSA");
          ...
          $KEY.initialize($BITS);
      - metavariable-comparison:
          comparison: $BITS < 2048
          metavariable: $BITS
    languages: [java]
    severity: WARNING`,
    ],
    target: `import java.security.KeyPairGenerator;

public class WeakRSA {

  static void rsaWeak() {
    // ruleid: use-of-weak-rsa-key
    KeyPairGenerator keyGen = KeyPairGenerator.getInstance("RSA");
    keyGen.initialize(512);
  }

  static void rsaOK() {
    // ok: use-of-weak-rsa-key
    KeyPairGenerator keyGen = KeyPairGenerator.getInstance("RSA");
    keyGen.initialize(2048);
  }
}`,
    language: "java",
    followupTitle: "Woohoo!",
    followup: (
      <div>
        <p>Brace yourself for some serious inception</p>
        <p>
          <span style={{ fontSize: "2em" }} role="img" aria-label=":smallbox:">
            📥
          </span>
        </p>
        <p>
          <span style={{ fontSize: "4em" }} role="img" aria-label=":midbox:">
            📥
          </span>
        </p>
        <p>
          <span style={{ fontSize: "6em" }} role="img" aria-label=":bigbox:">
            📥
          </span>
        </p>
      </div>
    ),
  },
  {
    title: "Metavariable pattern",
    content: (
      <div>
        <p>
          Do you remember back in the composition tutorial when we first
          introduced metavariable-regex and noted how it let you write code
          about your code about your code?
        </p>
        <p>
          Metavariable-pattern is like that, but maybe one layer deeper. Or
          maybe infinite layers deeper. We sort of lost track.
        </p>
        <em>
          Take a good hard look at this example to figure out what it is doing.
          There is a pattern-not embedded in the logic for a specific
          metavariable. This one was too hard to turn into a puzzle, so we are
          just giving it to you. Enjoy!
        </em>
      </div>
    ),
    pattern: `rules:
  - id: disallow-old-tls-versions2
    message: Detected an old tls version
    patterns:
      - pattern: |
          $CONST = require('crypto');
          ...
          $OPTIONS = $OPTS;
          ...
          https.createServer($OPTIONS, ...);
      - metavariable-pattern:
          metavariable: $OPTS
          patterns:
            - pattern-not: >
                {secureOptions: $CONST.SSL_OP_NO_SSLv2 | $CONST.SSL_OP_NO_SSLv3
                | $CONST.SSL_OP_NO_TLSv1}
    languages:
      - javascript
    severity: WARNING`,
    language: "js",
    answers: [
      `rules:
  - id: disallow-old-tls-versions2
    message: Detected an old tls version
    patterns:
      - pattern: |
          $CONST = require('crypto');
          ...
          $OPTIONS = $OPTS;
          ...
          https.createServer($OPTIONS, ...);
      - metavariable-pattern:
          metavariable: $OPTS
          patterns:
            - pattern-not: >
                {secureOptions: $CONST.SSL_OP_NO_SSLv2 | $CONST.SSL_OP_NO_SSLv3
                | $CONST.SSL_OP_NO_TLSv1}
    languages:
      - javascript
    severity: WARNING`,
    ],
    target: `function bad() {
  // ruleid:disallow-old-tls-versions2
  var constants = require('crypto');
  var sslOptions = {
  key: fs.readFileSync('/etc/ssl/private/private.key'),
  secureProtocol: 'SSLv23_server_method',
  secureOptions: constants.SSL_OP_NO_SSLv2 | constants.SSL_OP_NO_SSLv3
  };
  https.createServer(sslOptions);
}`,
    followupTitle: "Woohoo!",
    followup: (
      <div>
        <p>Ooof that was a lot.</p>
        <p>Why can't Semgrep just fix my code for me?</p>
        <p>...</p>
      </div>
    ),
  },
  {
    title: "Everybody loves Autofix",
    content: (
      <div>
        <p>
          Everybody loves <s>Raymond</s> autofix because it doesn't just tell
          you how to fix your code - it does it for you!
        </p>
        <p>
          Right now, autofix is mostly limited to single-line patterns and
          single-line fixes, but it still comes in quite handy.
        </p>
        <p>
          Semgrep's rule format supports a <code>fix:</code> key that supports
          metavariable replacement, much like message fields.
        </p>
        <p>
          <em>
            In this example try writing a <code>fix:</code> that matches the
            message description.
          </em>
        </p>
      </div>
    ),
    pattern: `rules:
  - id: use-sys-exit
    pattern: exit($X)
    message: |
      Use "sys.exit" over the python shell "exit" built-in. "exit" is not available on all Python implementations.
    languages: [python]
    severity: WARNING
  `,
    answers: [
      `rules:
  - id: use-sys-exit
    pattern: exit($X)
    message: |
      Use "sys.exit" over the python shell "exit" built-in. "exit" is not available on all Python implementations.
    languages: [python]
    severity: WARNING
    fix: sys.exit($X)
  `,
    ],
    target: `import sys

if False:
    # ok
    sys.exit(2)

if True:
    # ruleid: use-sys-exit
    exit(3)
`,
    followupTitle: "You did it!",
    followup: (
      <>
        <p>
          You can head over to{" "}
          <a href="https://semgrep.dev/s/O8xR/">the playground</a> to see
          autofix in action, or run rules with a fix on the CLI!
        </p>
        <span style={{ fontSize: "3em" }} role="img" aria-label=":train:">
          🧙🏽‍♂️
        </span>
      </>
    ),
  },
  {
    title: "Go forth and find things!",
    content: (
      <div>
        <h3>Congratulations!</h3>
        <p>
          You've reached the end of this tutorial. Semgrep has many other
          features that you can read about at <a href="/docs">the docs</a>.
        </p>
        <p>
          Here’s a favorite pattern that shows off what Semgrep can do. This
          rule searches for code injection in Django apps using{" "}
          <code>eval()</code> and has found real vulnerabilities! You can check
          it out in the playground{" "}
          <a href="https://semgrep.dev/playground/r/python.django.security.injection.code.user-eval">
            here
          </a>
          .
        </p>
      </div>
    ),
    pattern: `rules:
  - id: user-eval
    patterns:
    - pattern-inside: |
        def $F(...):
          ...
    - pattern-either:
      - pattern: eval(..., request.$W.get(...), ...)
      - pattern: |
          $V = request.$W.get(...)
          ...
          eval(..., $V, ...)
      - pattern: eval(..., request.$W(...), ...)
      - pattern: |
          $V = request.$W(...)
          ...
          eval(..., $V, ...)
      - pattern: eval(..., request.$W[...], ...)
      - pattern: |
          $V = request.$W[...]
          ...
          eval(..., $V, ...)
  `,
    answers: [
      `rules:
  - id: user-eval
    patterns:
    - pattern-inside: |
        def $F(...):
          ...
    - pattern-either:
      - pattern: eval(..., request.$W.get(...), ...)
      - pattern: |
          $V = request.$W.get(...)
          ...
          eval(..., $V, ...)
      - pattern: eval(..., request.$W(...), ...)
      - pattern: |
          $V = request.$W(...)
          ...
          eval(..., $V, ...)
      - pattern: eval(..., request.$W[...], ...)
      - pattern: |
          $V = request.$W[...]
          ...
          eval(..., $V, ...)
  `,
    ],
    target: `import django
from textwrap import dedent

def unsafe(request):
    # ruleid: user-eval
    code = request.POST.get('code')
    print("something")
    eval(code)

def unsafe_inline(request):
    # ruleid: user-eval
    eval(request.GET.get('code'))

def unsafe_dict(request):
    # ruleid: user-eval
    eval(request.POST['code'])

def safe(request):
    # ok
    code = """
    print('hello')
    """
    eval(dedent(code))
`,
    followupTitle: "That's All, Folks!",
    followup: (
      <>
        <p>
          Hop over to the <Link to="/playground">playground</Link> and put your
          newfound skills to good use, or <Link to="/explore">Explore</Link> the
          registry to see more examples of Semgrep rules that catch bugs and
          vulnerabilities every day!
        </p>
        <p>
          If you haven't already, please join the{" "}
          <a href={SLACK_INVITE_URL}>Community Slack </a> to communicate and
          collaborate with the Semgrep community.
        </p>
        <span style={{ fontSize: "3em" }} role="img" aria-label=":train:">
          💕
        </span>
      </>
    ),
  },
];
